import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Search, Users } from "lucide-react";
import { PageShell } from "./page-shell";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { EmptyState, ErrorState } from "@/components/common/empty-state";
import { useLeads } from "@/lib/queries";
import { formatRelative } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { LeadDrawer } from "./lead-drawer";
import type { Lead } from "@/lib/types";

type SortKey = "updated_at" | "full_name" | "phone" | "program";

export function LeadsPage() {
  const { data, isLoading, isError, refetch } = useLeads();
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("updated_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<Lead | null>(null);

  const filtered = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const q = query.trim().toLowerCase();
    const list = q
      ? data.filter((lead) => {
          const haystack = [
            lead.full_name,
            lead.phone,
            lead.telegram_username,
            lead.program,
            lead.alfacrm_lead_id,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(q);
        })
      : [...data];
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const va = (a[sortKey] ?? "") as string;
      const vb = (b[sortKey] ?? "") as string;
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return list;
  }, [data, query, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  return (
    <>
      <PageShell
        title="Leads"
        description="Every student the bot has talked to. Click a row to see their profile and conversation."
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, phone, telegram, CRM id..."
              className="pl-9"
              aria-label="Search leads"
            />
          </div>
          <div className="text-sm text-ink-muted">
            {Array.isArray(data) ? `${filtered.length} of ${data.length}` : "Loading"}
          </div>
        </div>

        <div className="rounded-lg border border-line bg-surface">
          {isLoading ? (
            <LeadsTableSkeleton />
          ) : isError ? (
            <ErrorState
              title="Couldn't load leads"
              description="The API didn't respond. Check your connection or retry."
              onRetry={() => void refetch()}
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Users}
              title={query ? "No leads match that search" : "No leads yet"}
              description={
                query
                  ? "Try a different keyword, or clear the search."
                  : "Leads will appear here as the bot qualifies students."
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHead
                    label="Name"
                    sortKey="full_name"
                    active={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortHead
                    label="Phone"
                    sortKey="phone"
                    active={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortHead
                    label="Program"
                    sortKey="program"
                    active={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <TableHead>CRM</TableHead>
                  <SortHead
                    label="Updated"
                    sortKey="updated_at"
                    active={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((lead) => (
                  <TableRow
                    key={lead.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelected(lead)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelected(lead);
                      }
                    }}
                    className="cursor-pointer"
                  >
                    <TableCell>
                      <div className="font-medium text-ink">{lead.full_name || "Unnamed lead"}</div>
                      {lead.telegram_username && (
                        <div className="text-xs text-ink-faint">@{lead.telegram_username}</div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-ink-muted">{lead.phone || "—"}</TableCell>
                    <TableCell>
                      {lead.program ? <Badge tone="outline">{lead.program}</Badge> : <span className="text-ink-faint">—</span>}
                    </TableCell>
                    <TableCell>
                      {lead.alfacrm_lead_id ? (
                        <span className="font-mono text-sm text-ink-muted">#{lead.alfacrm_lead_id}</span>
                      ) : (
                        <Badge tone="warning">no CRM</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-ink-muted">{formatRelative(lead.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </PageShell>

      <LeadDrawer lead={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function SortHead({
  label,
  sortKey,
  active,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: "asc" | "desc";
  onSort: (key: SortKey) => void;
}) {
  const isActive = active === sortKey;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider transition-colors",
          isActive ? "text-ink" : "text-ink-muted hover:text-ink"
        )}
      >
        {label}
        {isActive ? (
          dir === "asc" ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        ) : null}
      </button>
    </TableHead>
  );
}

function LeadsTableSkeleton() {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}
